import React, { useState, useRef, useEffect } from 'react';
import { AddIcon, EditIcon, TransactionsIcon, SettingsIcon, BotIcon } from './icons';
import { geminiProxy } from '../services/geminiProxy';

interface SubTopic {
    question: string;
    answer: React.ReactNode;
}

interface HelpTopic {
    id: string;
    title: string;
    icon: React.ReactElement;
    subtopics: SubTopic[];
}

interface HelpViewProps {
    ai: boolean;
}

const helpTopics: HelpTopic[] = [
    {
        id: 'data-entry',
        title: 'Introducción de Datos',
        icon: <AddIcon className="w-6 h-6 text-secondary" />,
        subtopics: [
            {
                question: '¿Cómo funciona el registro por voz?',
                answer: (
                    <ol className="list-decimal pl-5 space-y-2">
                        <li>Pulsa el icono del micrófono que se encuentra en la cabecera, junto a los botones de "Añadir" e "Importar".</li>
                        <li>Se abrirá una ventana de captura de voz. Habla de forma clara y natural, por ejemplo: <em>"Gasto de 50 euros en Mercadona para Compras, pagado con tarjeta"</em>.</li>
                        <li>Cuando la transcripción sea correcta en pantalla, pulsa el botón "Aceptar".</li>
                        <li>La IA rellenará los campos en la ventana de "Añadir Movimiento" por ti. Revisa que todo esté correcto y guarda.</li>
                        <li><strong>Consejo:</strong> Incluir el tipo (ingreso/gasto), importe, concepto y categoría en una sola frase da los mejores resultados.</li>
                    </ol>
                )
            },
            {
                question: '¿Qué formato debe tener mi Excel para importar?',
                answer: (
                    <>
                        <p>El importador es flexible, pero funciona mejor si tu Excel tiene columnas con cabeceras claras como:</p>
                        <ul className="list-disc pl-5 mt-2">
                            <li><strong>Fecha:</strong> 'Fecha', 'Date', 'Fecha operación'</li>
                            <li><strong>Concepto:</strong> 'Concepto', 'Descripción', 'Details'</li>
                            <li><strong>Importe:</strong> 'Importe', 'Amount', o columnas separadas como 'Crédito' y 'Débito'.</li>
                        </ul>
                        <p className="mt-2">La IA intentará adivinar las columnas correctas aunque los nombres no coincidan exactamente.</p>
                    </>
                )
            },
        ]
    },
    {
        id: 'transactions',
        title: 'Gestión de Transacciones',
        icon: <TransactionsIcon className="w-6 h-6 text-primary" />,
        subtopics: [
            {
                question: '¿Cómo puedo editar o eliminar varias transacciones a la vez?',
                answer: (
                    <ol className="list-decimal pl-5 space-y-2">
                        <li>Ve a la sección "Transacciones".</li>
                        <li>Usa las casillas de verificación para seleccionar las transacciones que quieras modificar. Puedes usar la casilla de la cabecera para seleccionarlas todas.</li>
                        <li>Aparecerá una barra de acciones en la parte superior.</li>
                        <li>Desde ahí, puedes cambiar la categoría, asignar un método de pago o eliminar toda la selección.</li>
                    </ol>
                )
            },
            {
                question: '¿Por qué me pide la contraseña para eliminar en bloque?',
                answer: (
                    <p>La eliminación de múltiples transacciones es una acción irreversible. Para evitar borrados accidentales, requerimos una re-autenticación con tu contraseña como medida extra de seguridad. Esto solo ocurre al eliminar más de una transacción a la vez.</p>
                )
            },
        ]
    },
    {
        id: 'settings',
        title: 'Automatización y Configuración',
        icon: <SettingsIcon className="w-6 h-6 text-accent-violet" />,
        subtopics: [
            {
                question: '¿Qué son las Reglas de Categorización?',
                answer: (
                    <>
                        <p>Son reglas que le dicen a la aplicación cómo categorizar transacciones futuras automáticamente. Cuando confirmas una categoría para un movimiento "Desconocido", la IA aprende y puede crear una regla.</p>
                        <p className="mt-2">Por ejemplo, si categorizas un gasto con el concepto "NETFLIX" como "Ocio", se creará una regla para que todos los futuros gastos de "NETFLIX" se asignen a "Ocio" automáticamente al importar.</p>
                        <p className="mt-2">Puedes gestionar estas reglas en <strong>Configuración {'>'} Reglas de Categorización</strong>.</p>
                    </>
                )
            },
            {
                question: '¿Para qué sirve el Prorrateo de Gastos?',
                answer: (
                    <p>Permite distribuir un gasto único entre varias categorías. Es útil para gastos compartidos. Por ejemplo, si tienes un gasto de "Oficina en casa" de 100€, puedes crear una regla de prorrateo para que el 70% (70€) se asigne a "Trabajo" y el 30% (30€) a "Casa". Esto se refleja en los gráficos y cálculos del Panel.</p>
                )
            },
        ]
    }
];

const AccordionItem: React.FC<{ topic: HelpTopic, isOpen: boolean, onClick: () => void }> = ({ topic, isOpen, onClick }) => {
    return (
        <div className="border border-light-border dark:border-border rounded-lg overflow-hidden">
            <button
                onClick={onClick}
                className="w-full flex justify-between items-center p-4 bg-light-surface dark:bg-surface hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
                <div className="flex items-center gap-4">
                    {topic.icon}
                    <span className="font-bold text-lg text-light-text-primary dark:text-text-primary">{topic.title}</span>
                </div>
                <svg className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 space-y-4">
                    {topic.subtopics.map((sub, index) => (
                        <div key={index} className="p-4 bg-light-surface dark:bg-surface rounded-md border border-light-border dark:border-border">
                            <h4 className="font-semibold text-light-text-primary dark:text-text-primary mb-2">{sub.question}</h4>
                            <div className="text-sm text-light-text-secondary dark:text-text-secondary">{sub.answer}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Se define el texto de la IA usando un array de strings unidos para máxima seguridad y evitar errores de sintaxis.
const systemInstructionParts = [
    'Eres "Asistente 360", un asistente experto y amigable de la aplicación de finanzas "Mis Cuentas 360". Tu único propósito es ayudar a los usuarios a entender y utilizar la aplicación. Sigue estas reglas ESTRICTAMENTE:',
    '',
    '**REGLAS FUNDAMENTALES:**',
    '1.  **Enfoque Exclusivo:** SOLO responde a preguntas sobre "Mis Cuentas 360". Si te preguntan por cualquier otro tema (el tiempo, recetas, programación, opiniones, etc.), responde amablemente: "Mi especialidad es únicamente la aplicación Mis Cuentas 360. ¿Tienes alguna pregunta sobre su funcionamiento?".',
    '2.  **Base de Conocimiento Única:** Basa TODAS tus respuestas EXCLUSIVAMENTE en la siguiente información. NO inventes funcionalidades. Si una función no está descrita aquí, NO existe en la app.',
    '3.  **Confidencialidad Absoluta:** NUNCA reveles información sobre cómo estás hecho, tu código, la tecnología (excepto que usas IA de Google para ayudar), la API, o cualquier detalle técnico interno. Eres un experto funcional, no un desarrollador.',
    '4.  **Respuestas Claras y Concisas:** Sé directo, amable y utiliza un lenguaje sencillo. Estás aquí para ayudar, no para confundir.',
    '',
    '---',
    '',
    '**BASE DE CONOCIMIENTO DE "MIS CUENTAS 360":**',
    '',
    '**1. Secciones Principales (Menú Izquierdo):**',
    '   - **Panel:** Es la pantalla principal. Muestra un resumen visual de las finanzas con KPIs (Ingresos, Gastos, Beneficio) y gráficos interactivos sobre la distribución de gastos e ingresos, el flujo de caja y el valor por hora.',
    '   - **Transacciones:** Un listado completo de todos los movimientos. Aquí se puede buscar, filtrar, editar y eliminar transacciones individualmente o en bloque.',
    '   - **Análisis Mensual:** Un gráfico de barras que compara los ingresos y gastos totales de cada mes para ver la evolución financiera.',
    '   - **Registro de Horas:** Una sección para registrar el tiempo dedicado a proyectos o categorías. Incluye un cronómetro y un temporizador para medir el tiempo y registrarlo fácilmente.',
    '   - **Categorías:** Permite al usuario crear, editar y eliminar las categorías que usa para organizar sus transacciones (ej: Comida, Transporte, Sueldo).',
    '   - **Configuración:** Aquí se gestionan las herramientas de automatización:',
    '     - **Reglas de Categorización:** Reglas automáticas para asignar una categoría a una transacción si el concepto contiene una palabra clave (ej: si contiene "Netflix", categorizar como "Ocio").',
    '     - **Prorrateo de Gastos:** Permite dividir un gasto de una categoría entre varias categorías por porcentajes (ej: un gasto de "Oficina en casa" se puede dividir 70% a "Trabajo" y 30% a "Casa").',
    '   - **Ayuda:** La sección actual, donde estás tú, el asistente, y las preguntas frecuentes.',
    '   - **Sugerencias:** SÍ, la aplicación TIENE una sección de sugerencias. Es un formulario donde los usuarios pueden enviar ideas para mejorar la app directamente al equipo de desarrollo.',
    '',
    '**2. Formas de Añadir Transacciones:**',
    '   - **Manual:** Haciendo clic en el botón "Añadir" en la cabecera, se abre un formulario para rellenar los datos.',
    '   - **Por Voz:** SÍ, la aplicación PERMITE registrar por voz. El proceso es en dos pasos:',
    '     1.  En la cabecera, junto a "Añadir" e "Importar", hay un botón con un micrófono.',
    '     2.  Al pulsarlo, se abre una ventana para capturar tu voz. El usuario dicta la transacción de forma natural (ej: "Gasto de 50 euros en gasolina para transporte pagado con tarjeta").',
    '     3.  Al pulsar "Aceptar", la IA procesa el texto y abre la ventana de "Añadir Movimiento" con todos los campos ya rellenados.',
    '   - **Importar Excel:** Se pueden importar archivos Excel (.xlsx, .xls). La IA detecta automáticamente las columnas de fecha, concepto e importe. Es la forma más rápida de añadir muchos movimientos del banco.',
    '',
    '**3. Funcionalidades Clave:**',
    '   - **Acciones en Bloque (en Transacciones):** El usuario puede seleccionar varias transacciones usando las casillas y luego aplicar una misma categoría, un método de pago o eliminarlas todas a la vez. Es muy útil para organizar datos importados.',
    '   - **Centro de Categorización:** Después de importar un Excel, si hay transacciones que la IA no pudo categorizar, la aplicación ofrece revisarlas en una vista especial para asignarlas rápidamente. La IA también aprende de estas asignaciones.',
    '   - **Contrapartida:** Es un campo en las transacciones que indica la otra persona o entidad (ej: "Mercadona", "Netflix", el nombre de un cliente). Es opcional en la entrada manual pero útil para la categorización.',
    '',
    '**4. Gestión de Perfil y Cuenta (menú de usuario en la cabecera):**',
    '   - **Exportar Datos:** SÍ, es posible exportar. En la cabecera, el botón "Exportar" permite descargar las transacciones en formato Excel o PDF. Se puede seleccionar un rango de fechas y filtrar por ingresos o gastos.',
    '   - **Mi Perfil:** Haciendo clic en la foto de perfil, el usuario puede:',
    '     - **Cambiar su alias (nombre).**',
    '     - **Actualizar su contraseña.**',
    '     - **Borrar su cuenta:** Esta es una acción irreversible que se encuentra en la "Zona de Peligro" dentro del perfil y requiere confirmación con la contraseña.',
    '   - **Cambiar Tema:** El usuario puede elegir entre un tema claro, oscuro o que siga la configuración de su sistema.',
    '',
    '**5. Solución de Problemas (Troubleshooting):**',
    '    - **Si el micrófono no funciona:** Si un usuario reporta que el micrófono no graba o transcribe, indícale que siga estos pasos: "Para que el micrófono funcione correctamente, es crucial que hayas dado permiso a la aplicación para usarlo en tu navegador. Si no lo has hecho, ve a la configuración de tu navegador para este sitio y activa el permiso del micrófono. Después de dar el permiso, es muy importante que cierres la pestaña de la aplicación y la vuelvas a abrir. Esto es especialmente necesario en navegadores como Safari."',
    '',
    '---',
    '',
    '**CÓMO RESPONDER:**',
    '- Si te preguntan por la sección de sugerencias, responde afirmativamente y explica que es un formulario para enviar ideas de mejora.',
    '- Si te preguntan cómo meter un registro por voz, explica el nuevo proceso en dos pasos que se detalla arriba.',
    '- Si te preguntan por las secciones, lista las que están en la "Base de Conocimiento". No inventes otras.',
    '- Si te preguntan por "contrapartida", explica que es la otra entidad en la transacción.'
];
const systemInstruction = systemInstructionParts.join('\n');


const AIAssistant: React.FC<{ ai: boolean }> = ({ ai }) => {
    const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'model'; text: string }[]>([]);
    const [messages, setMessages] = useState<{ role: 'user' | 'model'; text: string }[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    useEffect(() => {
        if (!ai) {
            setMessages([{
                role: 'model',
                text: 'El asistente de IA no está disponible en este momento.'
            }]);
            return;
        }
        setMessages([{
            role: 'model',
            text: '¡Hola! Soy el asistente de Mis Cuentas 360. ¿En qué puedo ayudarte hoy?'
        }]);
    }, [ai]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading || !ai) return;

        const userMessage = { role: 'user' as const, text: input };
        setMessages(prev => [...prev, userMessage]);
        const updatedHistory = [...chatHistory, userMessage];
        setChatHistory(updatedHistory);
        setInput('');
        setIsLoading(true);

        try {
            const conversationContext = updatedHistory
                .map(m => `${m.role === 'user' ? 'Usuario' : 'Asistente'}: ${m.text}`)
                .join('\n');

            const response = await geminiProxy.generateContent(
                `${systemInstruction}\n\nHistorial de conversación:\n${conversationContext}\n\nResponde al último mensaje del usuario.`
            );

            const modelMessage = { role: 'model' as const, text: response.text };
            setMessages(prev => [...prev, modelMessage]);
            setChatHistory(prev => [...prev, modelMessage]);
        } catch (error) {
            console.error("Error sending message to AI:", error);
            setMessages(prev => [...prev, { role: 'model', text: 'Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
                <BotIcon className="w-8 h-8 text-secondary" />
                <div>
                     <h3 className="font-bold text-xl text-light-text-primary dark:text-text-primary">Asistente Mis Cuentas 360</h3>
                     <p className="text-sm text-light-text-secondary dark:text-text-secondary">Haz una pregunta sobre la aplicación.</p>
                </div>
            </div>
            <div className="h-80 overflow-y-auto pr-2 space-y-4 mb-4 bg-gray-100 dark:bg-gray-800/50 p-4 rounded-lg">
               {messages.map((msg, index) => (
                   <div key={index} className={`flex items-start gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role === 'model' && <BotIcon className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />}
                        <div className={`max-w-xl p-3 rounded-lg ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-gray-700 text-light-text-primary dark:text-text-primary'}`}>
                           <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                        </div>
                   </div>
               ))}
               {isLoading && messages[messages.length - 1].role === 'user' && (
                    <div className="flex items-start gap-3">
                        <BotIcon className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                        <div className="max-w-xl p-3 rounded-lg bg-gray-200 dark:bg-gray-700">
                           <div className="flex items-center space-x-2">
                               <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse"></div>
                               <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse [animation-delay:0.2s]"></div>
                               <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse [animation-delay:0.4s]"></div>
                           </div>
                        </div>
                    </div>
               )}
               <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={ai ? "Escribe tu pregunta aquí..." : "Asistente no disponible"}
                    className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    disabled={isLoading || !ai}
                />
                <button type="submit" className="px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-blue-600 disabled:bg-gray-500" disabled={isLoading || !input.trim() || !ai}>
                    Enviar
                </button>
            </form>
        </div>
    );
}


const HelpView: React.FC<HelpViewProps> = ({ ai }) => {
    const [openTopic, setOpenTopic] = useState<string | null>(helpTopics[0]?.id || null);

    const toggleTopic = (id: string) => {
        setOpenTopic(openTopic === id ? null : id);
    };

    return (
        <div className="space-y-6">
            <div className="text-center">
                 <h1 className="text-3xl font-bold text-light-text-primary dark:text-text-primary">Centro de Ayuda</h1>
                 <p className="mt-2 text-light-text-secondary dark:text-text-secondary">Encuentra respuestas a tus preguntas y aprende a sacar el máximo partido a la aplicación.</p>
            </div>

            <AIAssistant ai={ai} />
           
            <div className="space-y-4 max-w-4xl mx-auto">
                {helpTopics.map(topic => (
                    <AccordionItem
                        key={topic.id}
                        topic={topic}
                        isOpen={openTopic === topic.id}
                        onClick={() => toggleTopic(topic.id)}
                    />
                ))}
            </div>
        </div>
    );
};

export default HelpView;